/**
 * Socios y membresías — AF §3 (RN-SOC-01 a RN-SOC-43).
 *
 * Lo que vive acá es lo que comparten backend, SPA del portal y app de socios:
 * la forma del alta, del padrón, de los documentos firmados y del apto médico.
 * Lo que sólo usa el backend queda en `backend/src/modules/socios/schemas.ts`.
 */
import { z } from "zod";
import { BENEFIT_KINDS, CORPORATE_CONDITION_KINDS, DOC_TYPES, INCOMPLETE_FLAGS, LEGAL_DOC_TYPES, MEDICAL_CERTIFICATE_STATUSES, MEMBER_STATUSES, PERSON_KINDS, PLATFORMS, WITHDRAWAL_MODES, } from "../enums.js";
import { dateISO, dateOnly, listQuery, moneySchema, objectId, reasonRequired } from "./base.js";
// ─────────────────────────────────────────────────────────────────────────────
// Piezas comunes
// ─────────────────────────────────────────────────────────────────────────────
/** RN-SOC-01: el documento es la clave única de persona (pasaporte para extranjeros). */
export const identityInput = z.object({
    docType: z.enum(DOC_TYPES).default("dni"),
    docNumber: z.string().trim().min(4).max(30),
});
/** RN-SOC-04: si falta, la ficha queda incompleta y recepción lo persigue. */
export const emergencyContactInput = z.object({
    name: z.string().trim().min(2).max(120),
    bond: z.string().trim().min(2).max(60),
    phone: z.string().trim().min(6).max(40),
});
/**
 * RN-SOC-09 — declaración de condición física. Cualquier «sí» marca la ficha
 * (`healthFlagged`) para que recepción lo converse en el primer ingreso presencial.
 * Es **dato de salud**: viaja acá y se guarda cifrado (modelo-datos §3.2).
 */
export const healthAnswersInput = z.object({
    injuries: z.boolean(),
    cardiac: z.boolean(),
    pregnancy: z.boolean(),
    notes: z.string().trim().max(2000).default(""),
});
/** ¿La declaración tiene algún «sí»? (RN-SOC-09) */
export function healthIsFlagged(answers) {
    return Boolean(answers.injuries || answers.cardiac || answers.pregnancy);
}
/** RN-SOC-15/16: el adulto responsable del menor, que firma. */
export const guardianInput = z.object({
    name: z.string().trim().min(2).max(120),
    dni: z.string().trim().min(4).max(30),
    phone: z.string().trim().min(6).max(40),
    email: z.string().trim().email().max(160).optional(),
    /** RN-SOC-16: AUTORIZO / NO AUTORIZO a retirarse por sus propios medios. */
    selfWithdrawalAuthorized: z.boolean().default(false),
});
/**
 * Aceptación de un consentimiento versionado (RN-SOC-24). `version` es el número
 * de `legalDocVersions`; si no viene, se firma la versión vigente de ese `docType`.
 */
export const consentAcceptanceInput = z.object({
    docType: z.enum(LEGAL_DOC_TYPES),
    version: z.number().int().min(1).optional(),
    /** Cesión de imagen SI/NO y retiro de menor AUTORIZO/NO viajan acá (RN-SOC-25/16). */
    value: z.boolean().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Padrón y alta presencial (RN-SOC-01..07)
// ─────────────────────────────────────────────────────────────────────────────
/** Filtros de la pantalla «Padrón de socios» (AF §3, pantalla Padrón). */
export const membersListQuery = listQuery.extend({
    /** Búsqueda: nombre, documento o número de socio. `q` de `listQuery`. */
    status: z.enum(MEMBER_STATUSES).optional(),
    /** Sede de venta de la membresía activa (RN-SOC-34); `branchId` filtra sede de origen. */
    saleBranchId: objectId.optional(),
    /** `expired` = apto vencido; `expiring` = vence dentro de `aptDays` (default 30). */
    apt: z.enum(["expired", "expiring", "missing", "in_grace", "valid"]).optional(),
    aptDays: z.coerce.number().int().min(1).max(365).default(30),
    incomplete: z.coerce.boolean().optional(),
    /** RN-SOC-25: el listado que el que maneja las redes consulta antes de publicar. */
    imageConsent: z.enum(["yes", "no", "unsigned"]).optional(),
    minors: z.coerce.boolean().optional(),
    /** RN-SOC-21/22/23: con convenio de empresa, canje o cortesía. */
    benefit: z.enum(["corporate", "courtesy", "barter", "discount", "any"]).optional(),
    corporateAgreementId: objectId.optional(),
    /** RN-SOC-43: fichas con candidato a duplicado. */
    duplicates: z.coerce.boolean().optional(),
    healthFlagged: z.coerce.boolean().optional(),
});
/** RN-SOC-01: chequeo de duplicado EN VIVO del mostrador, contra socios Y visitantes. */
export const checkIdentityQuery = z.object({
    docType: z.enum(DOC_TYPES).default("dni"),
    docNumber: z.string().trim().min(4).max(30),
});
/**
 * Alta presencial (RN-SOC-03/04/07). Obligatorios: nombre, documento, nacimiento,
 * celular y mail. Dirección, celular alternativo, obra social, lesiones y objetivos
 * son completables después: lo que falte queda en `incompleteFlags`.
 */
export const createMemberBody = z.object({
    identity: identityInput,
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    birthDate: dateISO,
    email: z.string().trim().toLowerCase().email().max(160),
    phone: z.string().trim().min(6).max(40),
    altPhone: z.string().trim().max(40).optional(),
    address: z.string().trim().max(200).optional(),
    emergencyContact: emergencyContactInput.optional(),
    healthInsurance: z.string().trim().max(120).optional(),
    injuries: z.string().trim().max(2000).optional(),
    goals: z.string().trim().max(2000).optional(),
    photoKey: z.string().trim().max(300).optional(),
    /** RN-SOC-15: menor con responsable. Se deriva de la edad, pero se puede forzar. */
    guardian: guardianInput.optional(),
    /** RN-SOC-06: huella ilegible permanente → `dni_only`, sin re-pedidos. */
    accessMode: z.enum(["fingerprint", "dni_only"]).optional(),
    /**
     * RN-SOC-30: reactivar la ficha existente en vez de crear otra. El mostrador lo
     * manda cuando el chequeo de duplicado ofreció reactivar y el recepcionista aceptó.
     */
    reactivateMemberId: objectId.optional(),
    /** RN-SOC-41: alta que nace de un visitante (conserva su historial). */
    fromVisitorId: objectId.optional(),
    /** RN-CRM-06: interesado del CRM que se convierte. */
    fromProspectId: objectId.optional(),
});
/**
 * Edición de ficha (RN-SOC-10). Documento, nombre y fecha de nacimiento **no están acá**:
 * van por `patchMemberSensitiveBody`, que exige encargado+ y motivo (D5).
 */
export const updateMemberBody = z.object({
    email: z.string().trim().toLowerCase().email().max(160).optional(),
    phone: z.string().trim().min(6).max(40).optional(),
    altPhone: z.string().trim().max(40).optional(),
    address: z.string().trim().max(200).optional(),
    emergencyContact: emergencyContactInput.optional(),
    healthInsurance: z.string().trim().max(120).optional(),
    injuries: z.string().trim().max(2000).optional(),
    goals: z.string().trim().max(2000).optional(),
    photoKey: z.string().trim().max(300).nullable().optional(),
    accessMode: z.enum(["fingerprint", "dni_only"]).optional(),
    guardian: guardianInput.optional(),
});
/** RN-SOC-10: documento, nombre y nacimiento exigen encargado+ y motivo. */
export const patchMemberSensitiveBody = z.object({
    identity: identityInput.optional(),
    firstName: z.string().trim().min(1).max(80).optional(),
    lastName: z.string().trim().min(1).max(80).optional(),
    birthDate: dateISO.optional(),
    reason: reasonRequired,
});
/** RN-SOC-35: baja explícita, con fecha (hoy o futura) y motivo de catálogo. */
export const terminateMemberBody = z.object({
    effectiveAt: dateISO.optional(),
    reason: reasonRequired,
    /** Código del catálogo `termination_reasons` (dato de churn). */
    reasonCode: z.string().trim().min(2).max(60).optional(),
});
/** RN-SOC-30: vuelve el inactivo o el dado de baja — la MISMA ficha. */
export const reactivateMemberBody = z.object({
    reason: z.string().trim().max(500).optional(),
});
/** RN-ACC-08.2: derecho de admisión. Sólo dueño, motivo obligatorio. */
export const admissionBanBody = z.object({
    active: z.boolean(),
    reason: reasonRequired,
});
// `enrollFingerprintBody` vive en `./acceso.ts` (RN-ACC-05 es de Acceso).
// ─────────────────────────────────────────────────────────────────────────────
// Apto médico (RN-SOC-11..14)
// ─────────────────────────────────────────────────────────────────────────────
/** Alta de un apto: la key la devuelve la URL firmada de subida. */
export const createMedicalCertificateBody = z.object({
    person: z.object({ kind: z.enum(PERSON_KINDS), id: objectId }).optional(),
    fileKey: z.string().trim().min(3).max(300),
    issuedAt: dateISO,
});
/** RN-SOC-11: la validación fija las fechas. */
export const validateMedicalCertificateBody = z.object({
    issuedAt: dateISO.optional(),
    /** Rechazar la carga (ilegible, vencida al subirla) con el porqué. */
    reject: z.boolean().optional(),
    note: z.string().trim().max(500).optional(),
});
/** Estado del apto tal como lo muestran la app y el semáforo (`null` = no se pudo ver). */
export const APT_STATES = [
    "none",
    "pending_validation",
    "valid",
    "expiring",
    "in_grace",
    "expired",
];
export const aptStatusResponse = z.object({
    status: z.enum(APT_STATES),
    certificateId: objectId.nullable(),
    issuedAt: dateISO.nullable(),
    expiresAt: dateISO.nullable(),
    graceUntil: dateISO.nullable(),
    daysToExpiry: z.number().int().nullable(),
    /** RN-SOC-13: agotada la gracia, el punto de acceso bloquea. */
    blocksAccess: z.boolean(),
    certificateStatus: z.enum(MEDICAL_CERTIFICATE_STATUSES).nullable(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Consentimientos versionados (RN-SOC-24..26)
// ─────────────────────────────────────────────────────────────────────────────
/** Publicar una versión nueva de un texto legal: NO altera las firmas viejas. */
export const publishLegalDocBody = z.object({
    docType: z.enum(LEGAL_DOC_TYPES),
    text: z.string().trim().min(20),
    effectiveFrom: dateISO.optional(),
    /** RN-SOC-26: la app pide re-aceptación en el próximo uso; no bloquea el ingreso. */
    requiresReacceptance: z.boolean().default(false),
});
export const legalDocsQuery = z.object({
    docType: z.enum(LEGAL_DOC_TYPES).optional(),
    /** Sólo la versión vigente de cada tipo. */
    currentOnly: z.coerce.boolean().default(false),
});
/** Firma desde la app: usuario autenticado, sin imagen (RN-SOC-24). */
export const signConsentsBody = z.object({
    consents: z.array(consentAcceptanceInput).min(1).max(10),
    /** RN-SOC-09: si viene, se firma también la declaración de condición física. */
    health: healthAnswersInput.optional(),
});
/** RN-SOC-25: la cesión de imagen se cambia desde la app y queda versionada. */
export const imageConsentBody = z.object({ value: z.boolean() });
// ─────────────────────────────────────────────────────────────────────────────
// Firma en la tablet kiosko (RN-SOC-07/24/42)
// ─────────────────────────────────────────────────────────────────────────────
/** Recepción manda un documento a la tablet: se emite `kiosk.signRequest`. */
export const createSignRequestBody = z.object({
    deviceId: objectId,
    docType: z.enum(LEGAL_DOC_TYPES),
    docVersionId: objectId.optional(),
    person: z.object({
        kind: z.enum(["member", "visitor", "guardian", "third_party"]),
        id: objectId.optional(),
        name: z.string().trim().max(120).optional(),
        dni: z.string().trim().max(30).optional(),
    }),
    /** RN-SOC-15: el responsable firma POR el menor. */
    guardianFor: objectId.optional(),
    /** Ficha de retiro por emergencia que espera esta firma (RN-SOC-42). */
    emergencyWithdrawalId: objectId.optional(),
});
/** La tablet devuelve la firma manuscrita y las opciones marcadas. */
export const kioskSignBody = z.object({
    /** Key de la imagen de la firma en S3 (subida con URL firmada). */
    signatureImageKey: z.string().trim().min(3).max(300),
    choices: z
        .object({
        imageConsent: z.boolean().optional(),
        minorSelfWithdrawal: z.boolean().optional(),
    })
        .optional(),
    health: healthAnswersInput.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Grupo familiar y pagador (RN-SOC-19/20)
// ─────────────────────────────────────────────────────────────────────────────
export const createFamilyGroupBody = z.object({
    address: z.string().trim().min(4).max(200),
    memberIds: z.array(objectId).min(1).max(20),
    payerMemberId: objectId.optional(),
    /** D5: sumar a alguien con otro domicilio exige encargado+ y motivo (RN-SOC-19). */
    addressExceptionReason: z.string().trim().min(5).max(500).optional(),
});
export const addFamilyMemberBody = z.object({
    memberId: objectId,
    /** Motivo de la excepción si el domicilio no coincide (RN-SOC-19, D5). */
    reason: z.string().trim().min(5).max(500).optional(),
});
export const setFamilyPayerBody = z.object({ payerMemberId: objectId.nullable() });
// ─────────────────────────────────────────────────────────────────────────────
// Convenios, canjes y cortesías (RN-SOC-21/22/23)
// ─────────────────────────────────────────────────────────────────────────────
export const createCorporateAgreementBody = z.object({
    company: z.object({
        name: z.string().trim().min(2).max(160),
        cuit: z.string().trim().max(20).optional(),
        contactName: z.string().trim().max(120).optional(),
        contactEmail: z.string().trim().email().max(160).optional().or(z.literal("")),
    }),
    condition: z.object({
        kind: z.enum(CORPORATE_CONDITION_KINDS),
        /** % sobre lista (`discount_pct`). */
        value: z.number().int().min(0).max(100).default(0),
        /** Cupo de membresías facturadas a la empresa (`company_billed`). */
        quota: z.number().int().min(0).default(0),
    }),
    validFrom: dateISO,
    validUntil: dateISO.nullable().optional(),
});
export const updateCorporateAgreementBody = createCorporateAgreementBody.partial().extend({
    status: z.enum(["active", "expired", "terminated"]).optional(),
});
export const agreementBeneficiaryBody = z.object({ memberId: objectId });
/** RN-SOC-22/23: cortesías, canjes y descuentos personales — SÓLO rol dueño, con motivo. */
export const createBenefitBody = z.object({
    memberId: objectId,
    kind: z.enum(BENEFIT_KINDS),
    /** 100 en cortesía; el % del descuento permanente; 0 en canje sin descuento. */
    pct: z.number().int().min(0).max(100).default(0),
    barterDescription: z.string().trim().max(500).optional(),
    reason: reasonRequired,
    validFrom: dateISO.optional(),
    validUntil: dateISO.nullable().optional(),
});
export const revokeBenefitBody = z.object({ reason: reasonRequired });
/** Reporte de cortesías con su costo mensual estimado (AF §3.6, pantalla Convenios). */
export const courtesyReportQuery = z.object({
    branchId: objectId.optional(),
    kind: z.enum(BENEFIT_KINDS).optional(),
    at: dateISO.optional(),
});
export const courtesyReportRow = z.object({
    benefitId: objectId,
    memberId: objectId,
    memberNumber: z.number().int(),
    memberName: z.string(),
    kind: z.enum(BENEFIT_KINDS),
    pct: z.number().int(),
    authorizedByUserId: objectId.nullable(),
    authorizedByName: z.string().nullable(),
    reason: z.string(),
    validFrom: dateISO,
    validUntil: dateISO.nullable(),
    /**
     * Costo mensual estimado: lo que la cuota valdría a lista vigente, por el %.
     * `null` = no se pudo valuar (sin membresía activa o sin precio en la lista).
     * Nunca cero: un `null` no es un regalo gratis, es un dato que falta.
     */
    monthlyCost: moneySchema.nullable(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Visitantes (RN-SOC-36..41)
// ─────────────────────────────────────────────────────────────────────────────
/** Alta express: desde la app a distancia o asistida en recepción (RN-SOC-37). */
export const createVisitorBody = z.object({
    platform: z.enum(PLATFORMS),
    identity: identityInput,
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    email: z.string().trim().toLowerCase().email().max(160).optional(),
    phone: z.string().trim().max(40).optional(),
    emergencyContact: emergencyContactInput.optional(),
    health: healthAnswersInput.optional(),
    consents: z.array(consentAcceptanceInput).max(10).optional(),
    branchOfOriginId: objectId.optional(),
});
export const updateVisitorBody = z.object({
    email: z.string().trim().toLowerCase().email().max(160).optional(),
    phone: z.string().trim().max(40).optional(),
    emergencyContact: emergencyContactInput.optional(),
    status: z.enum(["active", "blocked"]).optional(),
});
/** RN-SOC-40: recepción verifica vigencia y apto en la plataforma de origen. */
export const verifyVisitorBody = z.object({
    verifiedAt: dateISO.optional(),
    /** Si no viene, vence a `visitor.verificationMonths` (default 12). */
    expiresAt: dateISO.optional(),
});
export const visitorsListQuery = listQuery.extend({
    platform: z.enum(PLATFORMS).optional(),
    status: z.enum(["active", "blocked", "converted"]).optional(),
    verification: z.enum(["valid", "expired", "missing"]).optional(),
    healthFlagged: z.coerce.boolean().optional(),
});
/** Ingresos del mes por local: la base para conciliar la liquidación (RN-SOC-38). */
export const visitorEntriesQuery = z.object({
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Mes esperado YYYY-MM"),
    platform: z.enum(PLATFORMS).optional(),
    branchId: objectId.optional(),
    locationId: objectId.optional(),
});
/** RN-SOC-41: conversión visitante → socio, conservando el historial. */
export const convertVisitorBody = z.object({
    email: z.string().trim().toLowerCase().email().max(160),
    phone: z.string().trim().min(6).max(40),
    birthDate: dateISO,
    address: z.string().trim().max(200).optional(),
    emergencyContact: emergencyContactInput.optional(),
    healthInsurance: z.string().trim().max(120).optional(),
    guardian: guardianInput.optional(),
    branchOfOriginId: objectId.optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Ficha de retiro por emergencia (RN-SOC-42)
// ─────────────────────────────────────────────────────────────────────────────
export const createEmergencyWithdrawalBody = z.object({
    person: z.object({ kind: z.enum(PERSON_KINDS), id: objectId }),
    locationId: objectId,
    occurredAt: dateISO.optional(),
    description: z.string().trim().min(10).max(4000),
    emergencyServiceUsed: z.object({
        used: z.boolean(),
        serviceName: z.string().trim().max(160).optional(),
    }),
    withdrawalMode: z.enum(WITHDRAWAL_MODES),
    thirdParty: z
        .object({
        name: z.string().trim().min(2).max(120),
        dni: z.string().trim().min(4).max(30),
    })
        .optional(),
    /**
     * Caso borde del AF: el alta express de visitante no exige contacto de emergencia.
     * Si el afectado no lo tiene, la ficha lo pide **acá** y lo persiste en su ficha.
     */
    emergencyContact: emergencyContactInput.optional(),
});
/** La única «corrección» posible de una ficha cerrada (RN-SOC-42, D5). */
export const appendEmergencyNoteBody = z.object({
    text: z.string().trim().min(3).max(2000),
});
export const emergencyWithdrawalsQuery = listQuery.extend({
    personId: objectId.optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
    open: z.coerce.boolean().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Fusión de duplicados (RN-SOC-43)
// ─────────────────────────────────────────────────────────────────────────────
export const mergeCandidatesQuery = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    memberId: objectId.optional(),
});
/**
 * RN-SOC-43: descartar un candidato como falso positivo. **Motivo obligatorio**: dos
 * homónimos con la misma fecha de nacimiento son dos personas, y afirmarlo es una
 * decisión que se firma.
 */
export const dismissMergeCandidateBody = z.object({
    memberIds: z.array(objectId).min(2).max(10),
    reason: reasonRequired,
});
/** Buscador único de personas: socios y visitantes con la misma forma (AF §3, mostrador). */
export const searchPeopleQuery = z.object({
    q: z.string().trim().min(2).max(80),
    limit: z.coerce.number().int().min(1).max(50).optional(),
    kind: z.enum(["member", "visitor"]).optional(),
});
export const mergeMembersBody = z.object({
    /** Ficha que sobrevive. Por default, la del número de socio más antiguo. */
    survivorId: objectId,
    duplicateId: objectId,
    reason: reasonRequired,
});
export const undoMergeBody = z.object({ reason: reasonRequired });
// ─────────────────────────────────────────────────────────────────────────────
// App de socios (`/api/app/*`) — lo que pide `viva-app/docs/api-needs.md`
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Wizard completo de alta digital D4 (RN-SOC-08). Extiende el `register` mínimo de
 * `auth-app` con lo que el módulo Socios sabe guardar: emergencia, obra social,
 * declaración de condición física, consentimientos versionados y responsable del menor.
 * El **plan y el pago** son de §4: con `planId`, la ruta hace la compra de
 * `POST /api/app/me/plan-purchase` y la devuelve en `next`; `member.activated` lo emite
 * quien confirma el pago.
 */
export const appRegisterFullBody = z.object({
    identity: identityInput,
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    birthDate: dateISO,
    email: z.string().trim().toLowerCase().email().max(160),
    phone: z.string().trim().min(6).max(40),
    password: z.string().min(10).max(200),
    branchOfOriginId: objectId,
    address: z.string().trim().max(200).optional(),
    altPhone: z.string().trim().max(40).optional(),
    emergencyContact: emergencyContactInput.optional(),
    healthInsurance: z.string().trim().max(120).optional(),
    health: healthAnswersInput.optional(),
    consents: z.array(consentAcceptanceInput).max(10).optional(),
    guardian: guardianInput.optional(),
    /** Informativo: el cobro lo hace §4. Se guarda para que el checkout lo retome. */
    planId: objectId.optional(),
});
/** Alta express de visitante desde la app, con sesión (RN-SOC-37, D1). */
export const appRegisterVisitorBody = createVisitorBody.extend({
    password: z.string().min(10).max(200),
    branchOfOriginId: objectId,
});
/**
 * RN-SOC-10: desde la app se editan SÓLO contacto y emergencia.
 * Es **estricto** a propósito: si la app manda `identity`, `firstName` o `birthDate`,
 * el backend rechaza el cuerpo entero en vez de ignorarlos en silencio. Un campo que
 * se descarta calladito es un cambio que alguien creyó haber hecho.
 */
export const appUpdateProfileBody = z.strictObject({
    email: z.string().trim().toLowerCase().email().max(160).optional(),
    phone: z.string().trim().min(6).max(40).optional(),
    altPhone: z.string().trim().max(40).optional(),
    address: z.string().trim().max(200).optional(),
    emergencyContact: emergencyContactInput.optional(),
    healthInsurance: z.string().trim().max(120).optional(),
    injuries: z.string().trim().max(2000).optional(),
    goals: z.string().trim().max(2000).optional(),
    photoKey: z.string().trim().max(300).optional(),
});
/**
 * RN-SOC-35: pedido de baja desde la app — motivo OBLIGATORIO y fecha de hoy o futura.
 * `effectiveFrom` acepta el día calendario (`YYYY-MM-DD`, día operativo AR) o un instante
 * ISO; sin fecha, la baja se pide para hoy.
 */
export const appCancellationBody = z.object({
    reason: reasonRequired,
    effectiveFrom: z.union([dateOnly, dateISO]).optional(),
});
/**
 * Firma para subir un archivo del socio (apto, foto, foto del muro, adjunto de mensaje).
 * `wall_post` y `message_attachment` son sólo imagen (jpeg/png/webp) y su key nace con el
 * id de quien sube (`wall/<id>/…`, `messages/<id>/…`): así el muro puede verificar que
 * nadie publique un archivo ajeno.
 */
export const APP_UPLOAD_PURPOSES = [
    "medical_certificate",
    "member_photo",
    "signature",
    "wall_post",
    "message_attachment",
];
export const appUploadSignBody = z.object({
    contentType: z.enum(["image/jpeg", "image/png", "image/webp", "application/pdf"]),
    purpose: z.enum(APP_UPLOAD_PURPOSES),
});
/** Perfil que la app arma en el arranque (api-needs §2). */
export const appProfileResponse = z.object({
    person: z.object({
        kind: z.enum(PERSON_KINDS),
        id: objectId,
        firstName: z.string(),
        lastName: z.string(),
        memberNumber: z.number().int().nullable(),
        platform: z.enum(PLATFORMS).nullable(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
        altPhone: z.string().nullable(),
        address: z.string().nullable(),
        birthDate: dateISO.nullable(),
        photoUrl: z.string().nullable(),
        status: z.string(),
        branchOfOriginId: objectId.nullable(),
    }),
    emergencyContact: emergencyContactInput.nullable(),
    healthInsurance: z.string().nullable(),
    /** RN-SOC-04/07: lo que la app le persigue al socio. */
    missingFields: z.array(z.enum(INCOMPLETE_FLAGS)),
    fingerprintEnrolled: z.boolean(),
    accessMode: z.enum(["fingerprint", "dni_only"]),
    imageConsent: z.boolean().nullable(),
    minor: z
        .object({
        isMinor: z.boolean(),
        guardianName: z.string().nullable(),
        selfWithdrawalAuthorized: z.boolean(),
    })
        .nullable(),
    medical: aptStatusResponse,
    documents: z.array(z.object({
        id: objectId,
        docType: z.enum(LEGAL_DOC_TYPES),
        version: z.number().int(),
        signedAt: dateISO,
        medium: z.string(),
        value: z.boolean().nullable(),
    })),
    /** RN-SOC-26: versiones nuevas que piden re-aceptación. */
    pendingConsents: z.array(z.object({
        docType: z.enum(LEGAL_DOC_TYPES),
        version: z.number().int(),
        versionId: objectId,
        signedVersion: z.number().int().nullable(),
    })),
    familyGroup: z
        .object({
        id: objectId,
        address: z.string(),
        members: z.array(z.object({ id: objectId, name: z.string(), isPayer: z.boolean() })),
    })
        .nullable(),
});
// ─────────────────────────────────────────────────────────────────────────────
// Resumen de persona — frontera con Acceso (§6), Clases (§5) y Caja (§8)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Lo que `getMemberSummary` devuelve (`modules/socios/lookup.ts`).
 * Es la ficha mínima que el semáforo, la lista de clase y el mostrador necesitan
 * sin volver a consultar el padrón.
 */
export const memberSummary = z.object({
    id: objectId,
    memberNumber: z.number().int(),
    name: z.string(),
    /** Alias de `name` que usa el semáforo (Acceso §6). */
    fullName: z.string(),
    /** Iniciales para el avatar del semáforo: hoy no hay fotos de socios (AF §6). */
    initials: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    docNumber: z.string(),
    /** Foto opcional del socio (RN-SOC-05, PV-02); `null` si no cargó. */
    photoKey: z.string().nullable(),
    status: z.enum(MEMBER_STATUSES),
    /** RN-SOC-27: ancla inmutable + próximo vencimiento. `null` = sin membresía. */
    anchorDay: z.number().int().nullable(),
    nextDueDate: dateISO.nullable(),
    activePlan: z
        .object({ membershipId: objectId, planId: objectId, code: z.string(), name: z.string() })
        .nullable(),
    /** RN-SOC-15/16: menor y si está autorizado a retirarse solo. */
    minor: z.object({ isMinor: z.boolean(), selfWithdrawalAuthorized: z.boolean() }).nullable(),
    /** RN-SOC-09: la declaración tiene algún «sí». */
    healthFlagged: z.boolean(),
    /** RN-SOC-23 / RN-PLA-21: el % de cortesía que recepción ve en el check-in. */
    courtesyPct: z.number().int().nullable(),
    branchOfOriginId: objectId.nullable(),
    accessMode: z.enum(["fingerprint", "dni_only"]),
    incompleteFlags: z.array(z.enum(INCOMPLETE_FLAGS)),
    admissionBanned: z.boolean(),
    /** Badges listos para el semáforo (RN-ACC-08). */
    badges: z.array(z.string()),
});
//# sourceMappingURL=socios.js.map